import React from 'react';
import styled from 'styled-components';
import SEO from '../../components/common/SEO';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaUsers, FaMoneyBillWave, FaLock, FaChartLine, FaMobileAlt, FaFileAlt } from 'react-icons/fa';
import theme from '../../theme';

const DealerSection = styled.section`
  padding: 6rem 0 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.darkGray};
  max-width: 700px;
  margin: 0 auto;
`;

const ContentSection = styled.div`
  margin-bottom: 5rem;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 4rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  &:nth-child(even) {
    direction: rtl;
    @media (max-width: ${theme.breakpoints.tablet}) { direction: ltr; }
  }
  
  &:nth-child(even) > div { direction: ltr; }
`;

const ContentText = styled.div`
  p { margin-bottom: 1.5rem; line-height: 1.7; }
  ul {
    margin-bottom: 1.5rem; padding-left: 1.5rem;
    li { margin-bottom: 0.75rem; line-height: 1.7; }
  }
`;

const ContentImage = styled.div`
  img {
    width: 100%; height: auto;
    border-radius: ${theme.borderRadius};
    box-shadow: ${theme.boxShadow};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px; left: 0;
    width: 60px; height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem; margin-top: 3rem;
  @media (max-width: ${theme.breakpoints.desktop}) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: ${theme.breakpoints.mobile}) { grid-template-columns: 1fr; }
`;

const FeatureCard = styled.div`
  background-color: ${theme.colors.lightGray};
  padding: 2rem; border-radius: ${theme.borderRadius};
  transition: ${theme.transition}; text-align: center;
  &:hover { transform: translateY(-5px); box-shadow: ${theme.boxShadow}; }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem; color: ${theme.colors.primary};
  margin-bottom: 1rem; text-align: center;
  svg { width: 40px; height: 40px; }
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.fontSizes.medium}; margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.darkGray}; line-height: 1.6;
`;

const ProcessSection = styled.div`margin: 4rem 0;`;

const ProcessSteps = styled.div`
  display: flex; justify-content: space-between;
  margin-top: 3rem; position: relative;
  &:before {
    content: ''; position: absolute;
    top: 40px; left: 0; right: 0;
    height: 3px; background-color: ${theme.colors.lightGray}; z-index: 1;
  }
  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
    &:before { display: none; }
  }
`;

const ProcessStep = styled.div`
  width: 20%; text-align: center;
  position: relative; z-index: 2;
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 100%; margin-bottom: 2rem;
    display: flex; align-items: center; text-align: left;
  }
`;

const StepNumber = styled.div`
  width: 80px; height: 80px;
  background-color: ${theme.colors.primary}; color: ${theme.colors.white};
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 1.75rem; font-weight: 700; margin: 0 auto 1.5rem;
  @media (max-width: ${theme.breakpoints.tablet}) { margin: 0 1.5rem 0 0; min-width: 80px; }
`;

const StepTitle = styled.h4`
  margin-bottom: 0.75rem; font-size: ${theme.fontSizes.medium};
`;

const StepDescription = styled.p`
  font-size: ${theme.fontSizes.small}; color: ${theme.colors.darkGray};
`;

const CTASection = styled.section`
  background-color: ${theme.colors.secondary}; color: ${theme.colors.white};
  padding: 4rem 0; text-align: center;
`;

const CTATitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge}; margin-bottom: 1.5rem;
`;

const CTAText = styled.p`
  max-width: 700px; margin: 0 auto 2rem; font-size: ${theme.fontSizes.medium};
`;

const CTAButton = styled(Link)`
  display: inline-block; background-color: ${theme.colors.primary};
  color: ${theme.colors.white}; padding: 1rem 2rem;
  font-size: ${theme.fontSizes.medium}; font-weight: 600;
  border-radius: ${theme.borderRadius}; transition: ${theme.transition};
  &:hover { background-color: ${theme.colors.accent}; transform: translateY(-3px); box-shadow: 0 10px 20px rgba(0,0,0,0.1); }
`;

const DealerManagementPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const p = 'dealerManagementPage';

  const features = [
    { icon: <FaUsers />, key: 'profile' },
    { icon: <FaMoneyBillWave />, key: 'pricing' },
    { icon: <FaLock />, key: 'permissions' },
    { icon: <FaChartLine />, key: 'performance' },
    { icon: <FaMobileAlt />, key: 'mobile' },
    { icon: <FaFileAlt />, key: 'documents' },
  ];

  const stepKeys = ['step1', 'step2', 'step3', 'step4', 'step5'];

  return (
    <>
      <SEO titleKey={`${p}.seo.title`} descriptionKey={`${p}.seo.description`} />
      
      <DealerSection>
        <div className="container">
          <PageHeader>
            <PageTitle>{t(`${p}.pageTitle`)}</PageTitle>
            <PageSubtitle>{t(`${p}.pageSubtitle`)}</PageSubtitle>
          </PageHeader>
          
          <ContentSection>
            <TwoColumnLayout>
              <ContentText>
                <SectionTitle>{t(`${p}.section1.title`)}</SectionTitle>
                <p>{t(`${p}.section1.paragraph1`)}</p>
                <p>{t(`${p}.section1.paragraph2`)}</p>
                <ul>
                  <li>{t(`${p}.section1.bullet1`)}</li>
                  <li>{t(`${p}.section1.bullet2`)}</li>
                  <li>{t(`${p}.section1.bullet3`)}</li>
                  <li>{t(`${p}.section1.bullet4`)}</li>
                </ul>
              </ContentText>
              <ContentImage>
                <img src={`${process.env.PUBLIC_URL}/about-1.jpeg`} alt={t(`${p}.section1.imageAlt`)} loading="lazy" />
              </ContentImage>
            </TwoColumnLayout>
            
            <TwoColumnLayout>
              <ContentText>
                <SectionTitle>{t(`${p}.section2.title`)}</SectionTitle>
                <p>{t(`${p}.section2.paragraph1`)}</p>
                <p>{t(`${p}.section2.paragraph2`)}</p>
                <ul>
                  <li>{t(`${p}.section2.bullet1`)}</li>
                  <li>{t(`${p}.section2.bullet2`)}</li>
                  <li>{t(`${p}.section2.bullet3`)}</li>
                  <li>{t(`${p}.section2.bullet4`)}</li>
                </ul>
              </ContentText>
              <ContentImage>
                <img src={`${process.env.PUBLIC_URL}/dealer-1.jpeg`} alt={t(`${p}.section2.imageAlt`)} loading="lazy" />
              </ContentImage>
            </TwoColumnLayout>
            
            <SectionTitle style={{ textAlign: 'center', marginBottom: '2rem' }}>
              {t(`${p}.features.title`)}
            </SectionTitle>
            <FeatureGrid>
              {features.map((feature) => (
                <FeatureCard key={feature.key}>
                  <FeatureIcon>{feature.icon}</FeatureIcon>
                  <FeatureTitle>{t(`${p}.features.${feature.key}.title`)}</FeatureTitle>
                  <FeatureDescription>{t(`${p}.features.${feature.key}.description`)}</FeatureDescription>
                </FeatureCard>
              ))}
            </FeatureGrid>
            
            <ProcessSection>
              <SectionTitle style={{ textAlign: 'center', marginBottom: '2rem' }}>
                {t(`${p}.process.title`)}
              </SectionTitle>
              <ProcessSteps>
                {stepKeys.map((key, index) => (
                  <ProcessStep key={key}>
                    <StepNumber>{index + 1}</StepNumber>
                    <div>
                      <StepTitle>{t(`${p}.process.${key}.title`)}</StepTitle>
                      <StepDescription>{t(`${p}.process.${key}.description`)}</StepDescription>
                    </div>
                  </ProcessStep>
                ))}
              </ProcessSteps>
            </ProcessSection>
          </ContentSection>
        </div>
      </DealerSection>
      
      <CTASection>
        <div className="container">
          <CTATitle>{t(`${p}.cta.title`)}</CTATitle>
          <CTAText>{t(`${p}.cta.text`)}</CTAText>
          <CTAButton to={`/${lang}/demo`}>{t(`${p}.cta.button`)}</CTAButton>
        </div>
      </CTASection>
    </>
  );
};

export default DealerManagementPage;
